<template lang="pug">
f7-page#video-live-lesson
  // Loading Indicator
  f7-block(v-if="isLoading")
    f7-preloader

  // Error Message
  f7-block(v-if="errorMessage" color="red")
    p {{ errorMessage }}

  // Video Containers
  f7-block.video-area(strong inset v-show="!isLoading && !errorMessage")
    div.remote-video-wrapper(ref="remoteMediaContainer" :class="{ 'is-flipped': isRemoteVideoFlipped }")
      h4(v-if="!room || room.participants.size === 0") 参加待ち…
      div.timer(v-if="room && room.participants.size > 0") {{ elapsedTime }}

    div.local-video-wrapper(
      ref="localMediaContainer"
      @mousedown.prevent="startDrag"
      @touchstart.prevent="startDrag"
      :style="localVideoStyle"
      :class="{ dragging: isDragging }"
    )
      // Video/Audio elements will be attached here by Twilio logic

  // Controls (Optional)
  f7-block.centered-controls(v-if="!isLoading && !errorMessage && !room")
    f7-button.connect(v-if="!showReconnectButton" fill large @click="connectToRoom") 開始する
    f7-button.reconnect(v-if="showReconnectButton" fill large @click="reloadPage") 再接続

  div.in-room-controls-bottom(v-if="room")
    transition(name="fade")
      div.in-room-controls(v-if="showInRoomControls && room")
        f7-button.control-btn(round text-color="white" @click="toggleAudioMute")
          f7-icon(:f7="isAudioMuted ? 'mic_slash_fill' : 'mic_fill'")

        f7-button.control-btn(round @click="toggleRemoteVideoFlip")
          SvgIcon(:key="isRemoteVideoFlipped" :icon="isRemoteVideoFlipped ? 'btn_reverse_on' : 'btn_reverse_off'")

        f7-button.control-btn(round @click="disconnectFromRoom")
          SvgIcon(icon="end_call")

</template>

<style lang="scss" scoped>
.video-area {
  position: relative;
  width: 100%;
  // 画面いっぱいに広げるために高さを設定 (Navbarなどを考慮)
  height: 100vh; // Navbarの高さを仮定
  overflow: hidden; // はみ出しを隠す
  padding: 0 !important; // Framework7 の padding を上書き
  margin: 0 !important; // Framework7 の margin を上書き
  background-color: #111; // 背景色
}

.in-room-controls-bottom {
  right: 20px;
  bottom: 30px;
  left: auto; // Reset left
  width: auto; // Reset width
  z-index: 1100;
  pointer-events: none;
  position: absolute;
}

.in-room-controls {
  display: flex;
  flex-direction: column; // 縦並び
  align-items: center;
  gap: 16px;
  padding: 0;
  background: transparent;
  box-shadow: none;
  pointer-events: auto;
  transition: opacity 0.3s;

  .control-btn {
    width: 48px;
    height: 48px;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
    border: none;
    outline: none;

    // 透過処理
    opacity: 0.5;
    transition: opacity 0.3s;

    &:active,
    &:hover {
      opacity: 1;
    }

    // アイコンサイズ調整
    :deep(i) {
      font-size: 24px;
    }

    // SvgIcon調整
    :deep(.svg-icon) {
      width: 100%;
      height: 100%;
      display: block;
    }
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

// 相手のビデオ用ラッパー (画面全体)
.remote-video-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #222; // ビデオがない場合の背景
  display: flex; // タイトルを中央揃えにする場合など
  flex-direction: column;
  align-items: center;
  justify-content: center; // 中央に配置する場合

  h4 {
    position: absolute; // タイトルをビデオの上に表示
    top: 10px;
    left: 10px;
    color: rgba(255, 255, 255, 0.7);
    z-index: 1; // ビデオより手前に表示
    font-size: 0.8em;
    background-color: rgba(0, 0, 0, 0.5); // 読みやすくするため
    padding: 2px 5px;
    border-radius: 3px;
  }

  .timer {
    position: absolute;
    top: 10px;
    left: 10px;
    color: #fff;
    z-index: 1;
    font-size: 1.2em;
    background-color: rgba(0, 0, 0, 0.5);
    padding: 4px 8px;
    border-radius: 4px;
    font-weight: bold;
    font-variant-numeric: tabular-nums; // 数字の幅を等しくする
  }

  &.is-flipped {
    :deep(video) {
      // Target the video element inside
      transform: scaleX(-1);
    }
  }

  // 相手のビデオ要素のスタイル
  :deep(video),
  :deep(audio) {
    width: 100%;
    height: 100%;
    object-fit: cover; // コンテナに合わせて表示 (cover or contain)
    position: static; // absolute positioning はラッパーで行う
  }
}

// 自分のビデオ用ラッパー (左下オーバーレイ)
.local-video-wrapper {
  position: absolute;
  bottom: 20px; // 下からの位置
  left: 20px; // 左からの位置
  width: 100px; // 幅 (調整可能)
  height: 150px; // 高さ (調整可能)
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 8px; // 角を丸める
  overflow: hidden; // はみ出したビデオを隠す
  background-color: #333; // ビデオがない場合の背景
  z-index: 10; // 相手のビデオより手前に表示
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  cursor: grab; // Add grab cursor
  user-select: none; // Prevent text selection during drag
  touch-action: none; // Prevent scrolling on touch devices

  &.dragging {
    cursor: grabbing; // Change cursor while dragging
    // Optional: Add visual effect like slight shadow or scale
    // box-shadow: 0 5px 15px rgba(0,0,0,0.3);
    // transform: scale(1.02);
  }

  // 自分のビデオ要素のスタイル
  :deep(video),
  :deep(audio) {
    width: 100%;
    height: 100%;
    object-fit: cover; // コンテナに合わせて表示
    position: static;
  }
}
#framework7-root .button.connect,
#framework7-root .button.reconnect,
#framework7-root div.in-room-controls .button {
  background: $btn-primary;
  -webkit-tap-highlight-color: $btn-primary-hover;
  transition: transform 0.1s, opacity 0.1s;
  touch-action: manipulation; // ダブルタップズームによる300ms遅延を防止

  // 押した時のフィードバック (UX改善)
  &:active,
  &.active-state {
    opacity: 0.8;
    transform: scale(0.96); // 物理ボタンのように少し凹ませる
  }
}

#framework7-root .button.connect,
#framework7-root .button.reconnect {
  width: 100%;
  height: 56px;
  font-size: 18px;
  font-weight: bold;
  border-radius: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.centered-controls {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  background: rgba(34, 34, 34, 0.95);
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18);
  padding: 32px 24px 24px 24px;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}
</style>

<script>
import { connect, isSupported } from 'twilio-video'
import {
  ONE_TIME_LIVE_LESSON,
  ONE_TIME_LIVE_LESSON_ENDED,
  ONE_TIME_LIVE_LESSON_STARTED,
} from '@/graphql'
import SvgIcon from '@/components/atoms/SvgIcon'

export default {
  name: 'VideoLiveLesson',
  components: {
    SvgIcon,
  },
  data() {
    return {
      // Twilio specific data
      entryToken: null, // To store the fetched Twilio token
      // Generate a unique room name based on the lesson
      roomName: `lesson-room-${this.token}`,
      room: null, // To store the Twilio Room object
      isLoading: false, // Indicate loading state for token fetching/connection
      errorMessage: null, // Store potential errors
      isAudioMuted: false, // Flag for local audio mute state
      isRemoteVideoFlipped: false, // Flag for remote video flip state
      // --- Dragging State ---
      timerInterval: null,
      elapsedTime: '00:00',
      lessonStartTime: null,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dragCurrentX: typeof window !== 'undefined' ? window.innerWidth - 120 : 0, // 初期値は画面幅-120px
      dragCurrentTop: 0, // 初期値は右上にするためmountedで再計算
      offsetX: 0,
      offsetY: 0,
      containerHeight: 0,
      elementHeight: 150, // Store element height for convenience
      elementWidth: 100,
      showInRoomControls: true,
      inRoomControlsTimeout: null,
      CONTROLS_HIDE_DELAY_MS: 5000, // Delay for hiding in-room controls (in milliseconds)
      showReconnectButton: false,
    }
  },
  computed: {
    localVideoStyle() {
      return {
        left: `${this.dragCurrentX}px`,
        top: `${this.dragCurrentTop}px`, // Directly use dragCurrentTop
        cursor: this.isDragging ? 'grabbing' : 'grab',
      }
    },
  },
  methods: {
    // --- 1. Fetch Access Token ---
    async fetchToken(variables) {
      this.isLoading = true
      this.errorMessage = null
      this.$f7.dialog.preloader('接続中...')
      await this.$apollo
        .mutate(ONE_TIME_LIVE_LESSON(variables))
        .then((response) => {
          const { entryToken } = response.data.oneTimeLiveLesson
          this.entryToken = entryToken // Store the token
        })
        .catch((error) => {
          let primaryErrorMessage = '予期しないエラーが発生しました。'
          let customMessages = [] // カスタムメッセージ用配列

          if (error.graphQLErrors && error.graphQLErrors.length > 0) {
            const firstError = error.graphQLErrors[0]
            primaryErrorMessage = firstError.messages[0] || primaryErrorMessage // 標準メッセージ

            // extensions 内のカスタム messages を確認 (バックエンドが実装している場合)
            if (firstError.messages && Array.isArray(firstError.messages)) {
              customMessages = firstError.messages
              if (customMessages.length > 0) {
                // 例: カスタムメッセージを連結して表示
                primaryErrorMessage = customMessages.join('\n')
              }
            }
          } else if (error.networkError) {
            primaryErrorMessage = `ネットワークエラー: ${
              error.networkError.message || 'サーバーに接続できませんでした。'
            }`
          } else if (error.message) {
            // ApolloError 自体のメッセージ
            primaryErrorMessage = error.message
          }

          // UIに表示するエラーメッセージを設定
          this.errorMessage = `失敗:\n${primaryErrorMessage}`
          this.entryToken = null
        })
        .finally(() => {
          this.$f7.dialog.close()
          this.isLoading = false
        })
    },

    // --- 2. Connect to Twilio Room ---
    async connectToRoom() {
      if (this.room) {
        return
      }

      this.isLoading = true
      this.errorMessage = null

      try {
        // Connect to the room, letting Twilio manage track creation
        const room = await connect(this.entryToken, {
          name: this.roomName,
          audio: true,
          video: { facingMode: 'user' },
        })

        this.room = room
        this.isLoading = false
        this.errorMessage = null // Clear any previous error

        // Wait for the next DOM update cycle to ensure refs are available
        this.$nextTick(() => {
          // Attach local participant's tracks to the DOM
          const localMediaContainer = this.$refs.localMediaContainer
          if (localMediaContainer) {
            while (localMediaContainer.firstChild) {
              localMediaContainer.removeChild(localMediaContainer.firstChild)
            }
            room.localParticipant.tracks.forEach((publication) => {
              if (publication.track) {
                localMediaContainer.appendChild(publication.track.attach())
              }
            })
          }
        })

        // --- 3. Handle Participants ---
        // Attach existing participants' tracks
        room.participants.forEach(this.handleParticipantConnected)

        // 初回チェック
        this.checkParticipants(room)

        // Set up listeners for future participants and events
        room.on('participantConnected', (participant) => {
          this.handleParticipantConnected(participant)
          this.checkParticipants(room)
        })
        room.on('participantDisconnected', this.handleParticipantDisconnected)
        // Handle cleanup when disconnected from the room (e.g., kicked, network issue)
        room.on('disconnected', (disconnectedRoom, error) => {
          if (error) {
            this.errorMessage = `Disconnected due to error: ${error.message}`
          }
          // 自身が切断したタイミングでレッスン終了APIを呼び出す
          this.liveLessonEnded({ token: this.$f7route?.params?.token })
          this.cleanupRoom() // Perform cleanup
          // No need to check participants here as the room is gone
        })
      } catch (error) {
        this.errorMessage = `Connection failed: ${error.message}. ${this.entryToken}`
        this.isLoading = false
        // No local tracks to clean up if connect fails with this approach
        this.entryToken = null // Invalidate token on connection error
      }
    },

    // --- 4. Participant Connection Logic ---
    handleParticipantConnected(participant) {
      // Listen for tracks published by this participant
      participant.on('trackSubscribed', (track) =>
        this.handleTrackSubscribed(track, participant)
      )
      participant.on('trackUnsubscribed', (track) =>
        this.handleTrackUnsubscribed(track, participant)
      )

      // If participant already has tracks, subscribe to them
      participant.tracks.forEach((publication) => {
        if (publication.isSubscribed && publication.track) {
          this.handleTrackSubscribed(publication.track, participant)
        }
        // Note: Sometimes tracks might not be immediately available.
        // The 'trackSubscribed' event is more reliable.
      })
      this.startTimer()
    },

    // 参加者数をチェックしてAPIを呼び出す
    checkParticipants(room) {
      if (!room) return

      // 自分自身が接続されているかを確認
      const localParticipantIsConnected =
        room.localParticipant && room.localParticipant.state === 'connected'

      // リモート参加者数 + 自分自身
      const totalParticipants =
        room.participants.size + (localParticipantIsConnected ? 1 : 0)

      // 参加者が2名以上になったらレッスン開始APIを呼び出す
      if (totalParticipants >= 2) {
        this.liveLessonStarted({ token: this.$f7route?.params?.token })
      }
    },

    // Started
    async liveLessonStarted(variables) {
      try {
        await this.$apollo.mutate(ONE_TIME_LIVE_LESSON_STARTED(variables))
      } catch (error) {
        this.errorMessage = 'レッスン開始に失敗しました。'
      }
    },
    // Ended
    async liveLessonEnded(variables) {
      try {
        await this.$apollo.mutate(ONE_TIME_LIVE_LESSON_ENDED(variables))
      } catch (error) {
        this.errorMessage = 'レッスン終了に失敗しました。'
      }
    },

    handleParticipantDisconnected(participant) {
      // Remove all media elements associated with this participant
      this.removeAllParticipantTracks(participant.identity)
    },

    // --- 5. Track Subscription Logic ---
    handleTrackSubscribed(track, participant) {
      if (!track) return
      const remoteMediaContainer = this.$refs.remoteMediaContainer
      if (remoteMediaContainer) {
        const element = track.attach()
        element.dataset.identity = participant.identity // Tag element with participant identity
        remoteMediaContainer.appendChild(element)
      }
    },

    // eslint-disable-next-line no-unused-vars
    handleTrackUnsubscribed(track, _participant = null) {
      if (!track) return
      const elements = track.detach()
      elements.forEach((element) => element.remove())

      // Optional: Check if remote container is empty and add placeholder text
      const remoteMediaContainer = this.$refs.remoteMediaContainer
      if (remoteMediaContainer && remoteMediaContainer.childElementCount <= 1) {
        // Only h4 left
        // You could add a placeholder message here if desired
      }
    },

    // --- 6. Disconnect and Cleanup ---
    disconnectFromRoom() {
      if (this.room) {
        this.room.disconnect() // This triggers the 'disconnected' event listener
        // Cleanup is handled in the 'disconnected' event handler via cleanupRoom()
      } else {
        // No manually created tracks to clean up if room doesn't exist.
        this.cleanupLocalTracks()
      }
    },

    cleanupLocalTracks() {
      // With the new connection method, the SDK handles stopping and detaching tracks.
      // We just need to clear the container DOM.
      const localContainer = this.$refs.localMediaContainer
      if (localContainer) {
        while (localContainer.firstChild) {
          localContainer.removeChild(localContainer.firstChild)
        }
      }
      this.stopTimer()
    },

    removeAllParticipantTracks(identity) {
      const remoteMediaContainer = this.$refs.remoteMediaContainer
      if (remoteMediaContainer) {
        const children = Array.from(remoteMediaContainer.childNodes)
        children.forEach((node) => {
          // Check if it's an element node and has the matching data-identity
          if (node.nodeType === 1 && node.dataset.identity === identity) {
            node.remove()
          }
        })
        if (remoteMediaContainer.childElementCount <= 1) {
          // Only h4 left
        }
      }
    },

    cleanupRoom() {
      this.cleanupLocalTracks() // Stop and remove local media

      // Remove all remote media elements
      const remoteMediaContainer = this.$refs.remoteMediaContainer
      if (remoteMediaContainer) {
        // Remove all children. The conditional h4 will be re-added by Vue if necessary.
        while (remoteMediaContainer.firstChild) {
          remoteMediaContainer.removeChild(remoteMediaContainer.firstChild)
        }
      }

      this.room = null // Reset room state
      this.entryToken = null // Clear token
      this.isLoading = false // Reset loading state
      // Optionally keep or clear the error message
      // this.errorMessage = null;
      this.showReconnectButton = true
    },

    reloadPage() {
      // リロード直前にローディング表示へ切り替えて「反応した」ことを伝える
      this.isLoading = true
      window.location.reload()
    },

    // --- Drag Methods ---
    startDrag(event) {
      this.isDragging = true
      const element = this.$refs.localMediaContainer
      if (!element || !element.parentElement) return // Guard clause

      const parentRect = element.parentElement.getBoundingClientRect()
      this.containerHeight = parentRect.height // Update container height
      this.elementHeight = element.offsetHeight // Update element height
      this.elementWidth = element.offsetWidth // Update element width

      const isTouchEvent = event.type.startsWith('touch')
      const currentEvent = isTouchEvent ? event.touches[0] : event

      // Record starting position of the drag relative to the viewport
      this.dragStartX = currentEvent.clientX
      this.dragStartY = currentEvent.clientY

      // Record the element's ACTUAL current offset relative to the parent
      this.offsetX = element.offsetLeft
      this.offsetY = element.offsetTop // Use actual offsetTop

      // Add listeners (no changes needed here)
      if (isTouchEvent) {
        window.addEventListener('touchmove', this.doDrag, { passive: false })
        window.addEventListener('touchend', this.stopDrag)
        window.addEventListener('touchcancel', this.stopDrag)
      } else {
        window.addEventListener('mousemove', this.doDrag)
        window.addEventListener('mouseup', this.stopDrag)
      }
    },

    doDrag(event) {
      if (!this.isDragging) return

      event.preventDefault()

      const isTouchEvent = event.type.startsWith('touch')
      const currentEvent = isTouchEvent ? event.touches[0] : event

      const dx = currentEvent.clientX - this.dragStartX
      const dy = currentEvent.clientY - this.dragStartY

      // Calculate the new top and left positions
      let newX = this.offsetX + dx
      let newY = this.offsetY + dy // newY is the new 'top' position

      // --- Boundary checks ---
      const parentWidth =
        this.containerHeight > 0
          ? this.$refs.localMediaContainer.parentElement.offsetWidth
          : window.innerWidth // Get parent width safely
      const parentHeight = this.containerHeight

      // Ensure element dimensions are available
      const currentElementWidth = this.elementWidth || 100
      const currentElementHeight = this.elementHeight || 150

      newX = Math.max(0, Math.min(newX, parentWidth - currentElementWidth))
      newY = Math.max(0, Math.min(newY, parentHeight - currentElementHeight))
      // --- End Boundary checks ---

      // Update the current position directly using top and left
      this.dragCurrentX = newX
      this.dragCurrentTop = newY // Store the new 'top' position directly
    },

    stopDrag() {
      if (!this.isDragging) return
      this.isDragging = false

      // Remove listeners
      window.removeEventListener('mousemove', this.doDrag)
      window.removeEventListener('mouseup', this.stopDrag)
      window.removeEventListener('touchmove', this.doDrag)
      window.removeEventListener('touchend', this.stopDrag)
      window.removeEventListener('touchcancel', this.stopDrag)
    },
    // --- In-Room Control Methods ---
    toggleAudioMute() {
      if (this.room && this.room.localParticipant) {
        this.room.localParticipant.audioTracks.forEach((publication) => {
          if (this.isAudioMuted) {
            publication.track.enable()
          } else {
            publication.track.disable()
          }
        })
        this.isAudioMuted = !this.isAudioMuted
      } else {
        alert('Local audio track instance not found.')
      }
    },

    toggleRemoteVideoFlip() {
      this.isRemoteVideoFlipped = !this.isRemoteVideoFlipped
      // The actual flip is handled by CSS via a class binding or style binding
      // ここではコントロールを表示状態にする。自動非表示ロジックは handleShowInRoomControls で管理される。
      this.showInRoomControls = true
    },

    startTimer() {
      if (this.timerInterval) return
      this.lessonStartTime = Date.now()
      this.elapsedTime = '00:00'
      this.timerInterval = setInterval(() => {
        const diff = Date.now() - this.lessonStartTime
        const minutes = Math.floor(diff / 60000)
        const seconds = Math.floor((diff % 60000) / 1000)
        this.elapsedTime = `${String(minutes).padStart(2, '0')}:${String(
          seconds
        ).padStart(2, '0')}`
      }, 1000)
    },

    stopTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
        this.timerInterval = null
      }
    },

    handleShowInRoomControls() {
      if (!this.room) return
      this.showInRoomControls = true
      if (this.inRoomControlsTimeout) clearTimeout(this.inRoomControlsTimeout)
      this.inRoomControlsTimeout = setTimeout(() => {
        this.showInRoomControls = false
      }, this.CONTROLS_HIDE_DELAY_MS)
    },
  },
  async created() {
    if (!isSupported) {
      this.errorMessage = 'このブラウザはサポートされていません。'
    }
    if (!this.entryToken) {
      await this.fetchToken({
        token: this.$f7route?.params?.token,
      })
    }
  },
  mounted() {
    this.$nextTick(() => {
      const element = this.$refs.localMediaContainer
      if (element && element.parentElement) {
        const parentElement = element.parentElement
        this.containerHeight = parentElement.offsetHeight
        this.elementHeight = element.offsetHeight || 150
        this.elementWidth = element.offsetWidth || 100

        // 右上配置: dragCurrentX = 画面幅 - 120px, dragCurrentTop = 20px
        this.dragCurrentX = Math.max(0, window.innerWidth - 120)
        this.dragCurrentTop = 20
      }
    })
    window.addEventListener('touchstart', this.handleShowInRoomControls, {
      passive: true,
    })
    window.addEventListener('mousedown', this.handleShowInRoomControls)
  },
  beforeUnmount() {
    // Remove listener to prevent memory leaks
    this.disconnectFromRoom()
    window.removeEventListener('touchstart', this.handleShowInRoomControls)
    window.removeEventListener('mousedown', this.handleShowInRoomControls)
  },
}
</script>
